import { isProblemDetails } from './helper/ErrorHelper';
import { ensureResult, JsonFetch } from './helper/RequestHelper';

// A basic API client that can talk to ASP.Net Web API endpoints, network errors etc at thrown as ProblemDetails instances
// it includeds X-CSRF-TOKEN in each request so that it can work with cookies

export default class WebApiClient {
    constructor(
        public endpoint: string,
        public csrfToken: string,
        private onAuthRequired?: () => void
    ) {}

    /** Check for 401 and trigger the auth-required callback before re-throwing. */
    protected handlePossibleAuthError(e: { status?: number | null }): never {
        if (e.status === 401 && this.onAuthRequired) {
            this.onAuthRequired();
        }
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw e;
    }

    async JsonRequest<T>(
        path: string,
        method: string,
        body?: unknown,
        cancellationToken?: AbortSignal
    ): Promise<T | undefined> {
        try {
            return await JsonFetch<T>(path.startsWith('http') ? path : this.endpoint + path, method, {
                headers: {
                    'X-CSRF-TOKEN': this.csrfToken,
                },
                body: body,
                cancellationToken: cancellationToken,
            });
        } catch (e) {
            if (!isProblemDetails(e)) throw e;
            this.handlePossibleAuthError(e);
        }
    }

    async JsonGet<T>(path: string, cancellationToken?: AbortSignal): Promise<T> {
        return ensureResult(this.JsonRequest<T>(path, 'GET', undefined, cancellationToken));
    }

    async JsonDelete<T>(path: string): Promise<T | undefined> {
        return this.JsonRequest<T>(path, 'DELETE');
    }

    async JsonPost<T>(path: string, body: unknown): Promise<T> {
        return ensureResult(this.JsonRequest<T>(path, 'POST', body));
    }

    async JsonPut<T>(path: string, body: unknown): Promise<T> {
        return ensureResult(this.JsonRequest<T>(path, 'PUT', body));
    }

    async JsonPatch<T>(path: string, body: unknown): Promise<T> {
        return ensureResult(this.JsonRequest<T>(path, 'PATCH', body));
    }
}
